import React, { useEffect, useState, useContext } from "react"
import * as randomstring from "randomstring"
import { db, auth } from "../../utils/firebase"
import firebase from "firebase"
import "./gift-vouchers.scss"
import Papa from "papaparse"

// UI components
import Table from "../../components/structure/table/table"
import Title from "../../components/structure/title/title"
import Tile from "../../components/structure/tile/tile"
import Button from "../../components/ui/button/button"
import { LoadingIcon, PlusIcon, UploadIcon, SearchIcon, DownloadIcon } from "../../utils/svgs"
import VoucherRow from "./rows/voucher"
import Window from "../../components/structure/window/window"
import WindowCard from "../../components/ui/window-card/window-card"
import Input from "../../components/ui/inputs/input"
import Select from "../../components/ui/select/select"
import { AlertsContext } from "../../utils/providers/alerts"
import { exportGiftVouchers } from "./_utils/export"

// Returns the HTMl component for the Gift Vouchers
export default function GiftVouchers() {
	const [loading, setLoading] = useState(true)
	const [vouchers, setVouchers] = useState([])
	const [addingVoucher, setAddingVoucher] = useState(false)

	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [phone, setPhone] = useState("")
	const [email, setEmail] = useState("")
	const [value, setValue] = useState(null)
	const [voucherType, setVoucherType] = useState("")
	const [creating, setCreating] = useState(false)

	// Filter state
	const [filterType, setFilterType] = useState("")
	const [searchTerm, setSearchTerm] = useState("")
	const [filteredVouchers, setFilteredVouchers] = useState([])

	// Search state
	const [searchingDatabase, setSearchingDatabase] = useState(false)

	// Pagination state
	const [lastDoc, setLastDoc] = useState(null)
	const [hasMore, setHasMore] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)

	// Pre-fetched users map
	const [usersMap, setUsersMap] = useState({})

	// Bulk upload state
	const [bulkUploadOpen, setBulkUploadOpen] = useState(false)
	const [bulkUploadType, setBulkUploadType] = useState("")
	const [bulkUploadNote, setBulkUploadNote] = useState("")
	const [csvData, setCsvData] = useState([])
	const [csvFileName, setCsvFileName] = useState("")
	const [uploading, setUploading] = useState(false)

	// Export state
	const [exporting, setExporting] = useState(false)

	// Pull the alerts in for showing in the DOM
	const { pushAlert } = useContext(AlertsContext)

	useEffect(() => {
		// Pre-fetch all users first, then fetch vouchers
		fetchUsers().then(() => {
			fetchAndUpdateVouchers()
		})
	}, [])

	// Pre-fetch all users/staff
	const fetchUsers = async () => {
		const userDocs = await db.collection("users").get()
		const users = {}
		userDocs.docs.forEach((doc) => {
			users[doc.id] = doc.data()
		})
		setUsersMap(users)
	}

	// Filter vouchers when filterType or vouchers change
	useEffect(() => {
		// Skip if there's a search term (handled by search useEffect)
		if (searchTerm) return

		let filtered = vouchers

		// Filter by type
		if (filterType && filterType !== "ALL") {
			filtered = filtered.filter((voucher) => voucher.type === filterType)

			// If no local results, search database for this type
			if (filtered.length === 0) {
				fetchByType(filterType)
				return
			}
		}

		setFilteredVouchers(filtered)
	}, [filterType, vouchers])

	// Fetch vouchers by type from database
	const fetchByType = async (type) => {
		setSearchingDatabase(true)

		try {
			const voucherDocs = await db.collection("gift_vouchers").where("type", "==", type).limit(50).get()

			let results = voucherDocs.docs.map((doc) => ({
				...doc.data(),
				id: doc.id,
			}))

			// Sort by purchased date descending (client-side since we can't use orderBy without composite index)
			results.sort((a, b) => (b.purchased?.seconds || 0) - (a.purchased?.seconds || 0))

			setFilteredVouchers(results)
		} catch (err) {
			console.error("Fetch by type error:", err)
			setFilteredVouchers([])
		} finally {
			setSearchingDatabase(false)
		}
	}

	// Handle search - local first, then database
	useEffect(() => {
		if (!searchTerm) {
			// Reset to filtered vouchers when search is cleared
			let filtered = vouchers
			if (filterType && filterType !== "ALL") {
				filtered = filtered.filter((voucher) => voucher.type === filterType)
			}
			setFilteredVouchers(filtered)
			return
		}

		const search = searchTerm.toLowerCase()

		// First, search locally
		let localResults = vouchers.filter((voucher) => voucher.reference?.toLowerCase().includes(search))

		// Apply type filter if set
		if (filterType && filterType !== "ALL") {
			localResults = localResults.filter((voucher) => voucher.type === filterType)
		}

		if (localResults.length > 0) {
			setFilteredVouchers(localResults)
			setSearchingDatabase(false)
		} else {
			// No local results, search database
			setFilteredVouchers([])
			searchDatabase(search)
		}
	}, [searchTerm])

	// Search database for voucher by reference
	const searchDatabase = async (search) => {
		setSearchingDatabase(true)

		try {
			// Search for vouchers where reference contains the search term
			const voucherDocs = await db
				.collection("gift_vouchers")
				.orderBy("reference")
				.startAt(search.toUpperCase())
				.endAt(search.toUpperCase() + "\uf8ff")
				.limit(20)
				.get()

			if (voucherDocs.size > 0) {
				let results = voucherDocs.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}))

				// Apply type filter if set
				if (filterType && filterType !== "ALL") {
					results = results.filter((voucher) => voucher.type === filterType)
				}

				setFilteredVouchers(results)
			} else {
				setFilteredVouchers([])
			}
		} catch (err) {
			console.error("Database search error:", err)
		} finally {
			setSearchingDatabase(false)
		}
	}

	const fetchAndUpdateVouchers = async () => {
		setLoading(true)
		setLastDoc(null)
		setHasMore(false)

		db.collection("gift_vouchers")
			.orderBy("purchased", "desc")
			.limit(50)
			.get()
			.then((voucherDocs) => {
				const vouchers = voucherDocs.docs.map((voucherDoc) => {
					const voucher = voucherDoc.data()
					return {
						...voucher,
						id: voucherDoc.id,
					}
				})
				setVouchers(vouchers)
				setFilteredVouchers(vouchers)
				setLoading(false)

				// Track last document for pagination
				if (voucherDocs.docs.length === 50) {
					setLastDoc(voucherDocs.docs[voucherDocs.docs.length - 1])
					setHasMore(true)
				}
			})
	}

	// Load more vouchers
	const loadMoreVouchers = async () => {
		if (!lastDoc || loadingMore) return

		setLoadingMore(true)

		try {
			const voucherDocs = await db.collection("gift_vouchers").orderBy("purchased", "desc").startAfter(lastDoc).limit(50).get()

			const newVouchers = voucherDocs.docs.map((voucherDoc) => ({
				...voucherDoc.data(),
				id: voucherDoc.id,
			}))

			// Append to existing vouchers
			setVouchers((prev) => [...prev, ...newVouchers])

			// Update filtered vouchers based on current filter
			if (!filterType || filterType === "ALL") {
				setFilteredVouchers((prev) => [...prev, ...newVouchers])
			} else {
				const filteredNew = newVouchers.filter((v) => v.type === filterType)
				setFilteredVouchers((prev) => [...prev, ...filteredNew])
			}

			// Update pagination state
			if (voucherDocs.docs.length === 50) {
				setLastDoc(voucherDocs.docs[voucherDocs.docs.length - 1])
				setHasMore(true)
			} else {
				setHasMore(false)
			}
		} catch (err) {
			console.error("Load more error:", err)
		} finally {
			setLoadingMore(false)
		}
	}

	const createVoucher = async () => {
		setCreating(true)

		try {
			const clientID = await db
				.collection("clients")
				.where("email", "==", email.toLowerCase())
				.get()
				.then(async (clientDocs) => {
					// If there is a client document found
					if (clientDocs.size > 0) {
						return clientDocs.docs[0].id
					} else {
						// Make a new client account for the "FROM" person
						const newAccountID = await db
							.collection("clients")
							.add({
								first_name: firstName.charAt(0).toUpperCase() + firstName.slice(1),
								last_name: lastName.charAt(0).toUpperCase() + lastName.slice(1),
								email: email.toLowerCase(),
								phone: phone,
								created: firebase.firestore.FieldValue.serverTimestamp(),
							})
							.then((newClientDoc) => {
								return newClientDoc.id
							})

						// Add a log for the client account creation
						await db.collection(`clients/${newAccountID}/logs`).add({
							type: "CLIENT_CREATED",
							message: "Client account was created",
							created: firebase.firestore.FieldValue.serverTimestamp(),
						})

						// Finally, return the ID
						return newAccountID
					}
				})

			// Generate a reference
			const voucherReference = randomstring.generate({
				length: 12,
				charset: "123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ",
			})

			// Add a new gift voucher document to the database
			await db
				.collection("gift_vouchers")
				.add({
					purchased: firebase.firestore.FieldValue.serverTimestamp(),
					issued_by: auth.currentUser.uid,
					purchased_by: null,
					purchased_for: clientID,
					value: parseInt(value),
					reference: `GHDV-${voucherReference}`,
					status: "PAID",
					bookingID: null,
					type: voucherType,
				})
				.then((newVoucherDoc) => {
					return newVoucherDoc.id
				})
		} catch (err) {
			console.log(err)
		} finally {
			fetchAndUpdateVouchers()

			setCreating(false)
			setAddingVoucher(false)

			// Reset form fields
			setFirstName("")
			setLastName("")
			setPhone("")
			setEmail("")
			setValue(null)
			setVoucherType("")
		}
	}

	// Handle CSV file selection and parsing
	const handleCsvUpload = (e) => {
		const file = e.target.files[0]
		if (!file) return

		setCsvFileName(file.name)

		Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			complete: (results) => {
				// Validate that the CSV has the required columns
				const headers = results.meta.fields || []
				if (!headers.includes("code") || !headers.includes("value")) {
					pushAlert({
						type: "ERROR",
						title: "Invalid CSV format. Please ensure the CSV has 'code' and 'value' columns.",
					})
					setCsvData([])
					setCsvFileName("")
					return
				}

				// Filter out rows with empty code or value
				const validRows = results.data.filter((row) => row.code && row.value)
				setCsvData(validRows)

				pushAlert({
					type: "SUCCESS",
					title: `Loaded ${validRows.length} vouchers from CSV.`,
				})
			},
			error: (error) => {
				console.error("CSV parsing error:", error)
				pushAlert({
					type: "ERROR",
					title: "Error parsing CSV file.",
				})
			},
		})
	}

	// Bulk upload vouchers from CSV
	const bulkUploadVouchers = async () => {
		if (csvData.length === 0 || !bulkUploadType) return

		setUploading(true)

		try {
			// Create vouchers for each row in the CSV
			for (const row of csvData) {
				await db.collection("gift_vouchers").add({
					purchased: firebase.firestore.FieldValue.serverTimestamp(),
					issued_by: auth.currentUser.uid,
					purchased_by: null,
					purchased_for: null,
					value: parseInt(row.value),
					reference: row.code,
					status: "PAID",
					bookingID: null,
					type: bulkUploadType,
					note: bulkUploadNote || null,
				})
			}

			pushAlert({
				type: "SUCCESS",
				title: `Successfully uploaded ${csvData.length} vouchers.`,
			})

			// Reset and close
			setCsvData([])
			setCsvFileName("")
			setBulkUploadType("")
			setBulkUploadNote("")
			setBulkUploadOpen(false)
			fetchAndUpdateVouchers()
		} catch (err) {
			console.error("Bulk upload error:", err)
			pushAlert({
				type: "ERROR",
				title: "Error uploading vouchers. Please try again.",
			})
		} finally {
			setUploading(false)
		}
	}

	// Download the whole gift_vouchers collection as a spreadsheet
	const exportVouchers = async () => {
		setExporting(true)

		try {
			const { vouchers: voucherCount } = await exportGiftVouchers(usersMap)

			pushAlert({
				type: "SUCCESS",
				title: `Exported ${voucherCount} vouchers.`,
			})
		} catch (err) {
			console.error("Export error:", err)
			pushAlert({
				type: "ERROR",
				title: "Error exporting vouchers. Please try again.",
			})
		} finally {
			setExporting(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title>
				<h1>Gift Vouchers</h1>
			</Title>

			<div className="vouchers-toolbar">
				<div className="toolbar-search">
					<Input
						type="text"
						placeholder="Search by reference..."
						value={searchTerm}
						onChange={setSearchTerm}
						icon={<SearchIcon />}
					/>
				</div>

				<div className="toolbar-filter">
					<Select
						placeholder="Filter by type"
						options={{
							ALL: "All Types",
							INT_COMPENSATION: "Internal Compensation",
							EXT_PARTNER: "External Partner",
							EXT_MARKETING: "External Marketing",
							EXT_PURCHASED: "EXT/Purchased",
						}}
						selected={filterType}
						onSelect={(selected) => setFilterType(selected.option)}
					/>
				</div>

				<div className="toolbar-actions">
					<Button
						label="Export"
						small={true}
						icon={<DownloadIcon />}
						loading={exporting}
						loadingText="Exporting..."
						disabled={loading}
						onClick={() => exportVouchers()}
					/>

					<Button
						label="Bulk Upload"
						small={true}
						icon={<UploadIcon />}
						onClick={() => setBulkUploadOpen(true)}
					/>

					<Button
						label="Create Voucher"
						small={true}
						icon={<PlusIcon />}
						onClick={() => setAddingVoucher(true)}
					/>
				</div>
			</div>

			{searchingDatabase && (
				<div className="searching-database-notice">
					<LoadingIcon />
					<span>Searching database...</span>
				</div>
			)}

			<Table
				className="bookings-table"
				headings={["Date purchased/issued", "Reference", "Issued by", "Purchased by", "Purchased for", "Type", "Value", "Status", "Booking ref", ""]}
				noResults={filteredVouchers.length === 0 && !searchingDatabase}
				noResultsMessage={searchTerm ? `No vouchers found matching "${searchTerm}"` : "No vouchers found"}>
				{filteredVouchers.map((voucher, index) => (
					<VoucherRow
						key={voucher.id}
						{...voucher}
						usersMap={usersMap}
						onDelete={() => fetchAndUpdateVouchers()}
					/>
				))}

				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}
			</Table>

			{hasMore && !searchTerm && (
				<div className="show-more-wrapper">
					<Button
						label={loadingMore ? "Loading..." : "Show More"}
						small={true}
						onClick={() => loadMoreVouchers()}
						loading={loadingMore}
						loadingText="Loading..."
					/>
				</div>
			)}

			{addingVoucher && (
				<Window
					title="Create a new voucher"
					close={() => setAddingVoucher(false)}>
					<WindowCard
						title="Client details"
						subtitle="Details for the client who will have this assigned to their account.">
						<div className="input-grid cols-2">
							<Input
								type="text"
								label="First name:"
								value={firstName}
								onChange={setFirstName}
								placeholder="First name:"
							/>
							<Input
								type="text"
								label="Last name:"
								value={lastName}
								onChange={setLastName}
								placeholder="Last name:"
							/>
							<Input
								type="tel"
								label="Phone:"
								value={phone}
								onChange={setPhone}
								placeholder="Phone:"
							/>
							<Input
								type="email"
								label="Email:"
								value={email}
								onChange={setEmail}
								placeholder="Email:"
							/>

							<div className="span-2">
								<Select
									placeholder="Select voucher type"
									options={{
										INT_COMPENSATION: "INT/Compensation",
										EXT_PARTNER: "EXT/Partner",
										EXT_MARKETING: "EXT/Marketing",
										EXT_PURCHASED: "EXT/Purchased",
									}}
									selected={voucherType}
									onSelect={(selected) => setVoucherType(selected.option)}
									showLabel={true}
									label="Voucher type:"
								/>
							</div>

							<div className="span-2">
								<Input
									value={value}
									onChange={setValue}
									symbol="£"
									placeholder="Voucher value:"
								/>
							</div>
						</div>
					</WindowCard>

					<Button
						label="Create voucher"
						className="window-card-button"
						onClick={() => createVoucher()}
						loading={creating}
						loadingText="Creating voucher..."
						disabled={!firstName || !lastName || !phone || !email || !value || !voucherType}
					/>
				</Window>
			)}

			{bulkUploadOpen && (
				<Window
					title="Bulk Upload Vouchers"
					close={() => {
						setBulkUploadOpen(false)
						setCsvData([])
						setCsvFileName("")
						setBulkUploadType("")
						setBulkUploadNote("")
					}}>
					<WindowCard
						title="CSV Upload"
						subtitle="Upload a CSV file with 'code' and 'value' columns.">
						<div className="input-grid cols-1">
							<div className="csv-upload-wrapper">
								<input
									type="file"
									accept=".csv"
									onChange={handleCsvUpload}
									id="csv-upload"
									style={{ display: "none" }}
								/>
								<label
									htmlFor="csv-upload"
									className="csv-upload-label">
									<UploadIcon />
									<span>{csvFileName || "Choose CSV file..."}</span>
								</label>
							</div>

							{csvData.length > 0 && (
								<div className="csv-preview">
									<p>
										<strong>{csvData.length}</strong> voucher{csvData.length !== 1 ? "s" : ""} ready to upload
									</p>
									<p style={{ marginTop: "4px", fontSize: "13px" }}>
										Value range: £{Math.min(...csvData.map((r) => parseInt(r.value))).toLocaleString()} - £{Math.max(...csvData.map((r) => parseInt(r.value))).toLocaleString()}
									</p>
									<p style={{ marginTop: "2px", fontSize: "13px" }}>Total value: £{csvData.reduce((sum, r) => sum + parseInt(r.value), 0).toLocaleString()}</p>
								</div>
							)}

							<Select
								placeholder="Select voucher type"
								options={{
									INT_COMPENSATION: "INT/Compensation",
									EXT_PARTNER: "EXT/Partner",
									EXT_MARKETING: "EXT/Marketing",
									EXT_PURCHASED: "EXT/Purchased",
								}}
								selected={bulkUploadType}
								onSelect={(selected) => setBulkUploadType(selected.option)}
								showLabel={true}
								label="Voucher type:"
							/>

							<Input
								type="text"
								placeholder="Note (e.g. what are these vouchers for?):"
								value={bulkUploadNote}
								onChange={setBulkUploadNote}
							/>
						</div>
					</WindowCard>

					<Button
						label="Upload Vouchers"
						className="window-card-button"
						onClick={() => bulkUploadVouchers()}
						loading={uploading}
						loadingText="Uploading..."
						disabled={csvData.length < 1 || bulkUploadType === ""}
					/>
				</Window>
			)}
		</Tile>
	)
}
